import React, { useState, useEffect, useRef } from 'react';
import { Message } from 'semantic-ui-react';

/**
 * DelayedMessage - A wrapper for Semantic UI Message that only displays after a delay
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Whether the message should be visible
 * @param {number} props.delay - Delay in milliseconds before showing the message (default: 300ms)
 * @param {string} props.type - Message type (info, warning, error, success, etc.)
 * @param {string} props.header - Message header
 * @param {React.ReactNode} props.children - Message content
 * @param {Object} props.rest - Additional props to pass to the Message component
 */
const DelayedMessage = ({
  visible,
  delay = 300,
  type,
  header,
  children,
  ...rest
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // When visible becomes true, start the timer
    if (visible) {
      timerRef.current = setTimeout(() => {
        setShouldRender(true);
      }, delay);
    } else {
      // When visible becomes false, clear the timer and hide the message
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setShouldRender(false);
    }

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [visible, delay]);

  // Only render the message if shouldRender is true
  if (!shouldRender) return null;

  // Create the appropriate message based on type
  const messageProps = {
    ...rest
  };

  if (type) {
    messageProps[type] = true;
  }

  return (
    <Message {...messageProps}>
      {header && <Message.Header>{header}</Message.Header>}
      {children}
    </Message>
  );
};

export default DelayedMessage;
